import { BehaviorSubject, Observable, throwError } from 'rxjs';
import { catchError, map, tap } from 'rxjs/operators';

import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { User } from 'app/classes/user';
import { environment } from 'environments/environment';

export interface SearchResult {
  users: User[];
  currentPage: number;
  limitPerPage: number;
  totalPages: number;
  hasNextPage: boolean;
  total: number;
}


/**
 * Convert response for a user into a User object.
 *
 * @param userData - server response
 * @returns User
 */
function convertToUser(userData: any): User {
  const newUser = new User()
    .setUUID(userData.uuid)
    .setFirstName(userData.firstname)
    .setLastName(userData.lastname);

  newUser.email = userData.email;
  newUser.invited = userData.invited;

  return newUser;
}

@Injectable({
  providedIn: 'root'
})
export class UserSearchService {
  private resource = 'User/search';
  users: Observable<User[]>;
  private _loadingUsers = new BehaviorSubject(false);

  get loadingUsers(): Observable<boolean> {
    return this._loadingUsers.asObservable();
  }

  constructor(private http: HttpClient) { }

  handleError(e: any): Observable<never> {
    return throwError(e.message || 'Server error');
  }

  private get<T>(route: string) {
    return this.http.get<T>(this.buildRoute(route));
  }

  private buildRoute(route: string) {
    return environment.applySubstitutions(environment.api_url) + 'api' + route;
  }

  searchUsers(search: string, pageNumber: number, limitPerPage: number, excludeBasicUsers: boolean = false): Observable<SearchResult> {
    this._loadingUsers.next(true);
    let url = `/${this.resource}?search=${search}&pageNumber=${pageNumber}&limitPerPage=${limitPerPage}`;
    if (excludeBasicUsers) {
      url = `${url}&excludeBasicUsers=True`;
    }

    return this.get<SearchResult>(url)
      .pipe(
        map((response: any) => {
          return {
            ...response,
            users: response.users.map(convertToUser),
          };
        }),
        catchError(this.handleError.bind(this)),
        tap(() => this._loadingUsers.next(false))
      );
  }

  fetchRawUsers(search: string, pageNumber: number, limitPerPage: number, sortField: string, sortOrder: string): Observable<SearchResult> {
    this._loadingUsers.next(true);
    return this.get<SearchResult>(`/${this.resource}?search=${search}&pageNumber=${pageNumber}&limitPerPage=${limitPerPage}&sort=${sortField}&order=${sortOrder}`)
      .pipe(
        map((response: any) => {
          return {
            ...response,
            users: response.users,
          };
        }),
        catchError(this.handleError.bind(this)),
        tap(() => this._loadingUsers.next(false))
      );
  }
}
